import React, { useState, useEffect, useRef } from 'react';
import { Button, Modal } from 'antd';
import TimezoneOperationhours from 'components/Calendar/TimezoneOperationhours';
import DatesOverride from 'components/Calendar/DatesOverride';
import AuthButton from 'pages/dashboard/CalendarManagePage/components/AuthButton';
import cls from 'classnames';
import s from './s.module.less';
import { PATH } from 'constants/path';
import googleCalendarIcon from 'assets/calendar/google-calendar.png';
import Step1 from './step1.png';
import Step2 from './step2.png';
import { shouldUseKlarityUi } from 'utils/common';

interface WelcomeModalProps {
    isOpen: boolean;
    onClose: () => void;
    step?: number;
    showBackButton?: boolean;
    isExistUser?: boolean;
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({ isOpen, onClose, step, showBackButton = true, isExistUser }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const isShowKlarityUi = shouldUseKlarityUi();
    const timeRef = useRef();
    const calendarLabel = shouldUseKlarityUi() ? 'Klarity Calendar' : 'Kiwi Calendar';

    useEffect(() => {
        if (step) {
            setCurrentStep(step);
        }
    }, [step]);

    const renderSecondStep = () => {
        return (
            <div className={s.timeContainer}>
                <div className={s.welcomeContent}>
                    <h2 className={s.welcomeTitle}>Set up your working hours</h2>
                    <p className={s.welcomeText}>
                        Set your working hours and availability to ensure appointments are scheduled at times that work best for you. This will help avoid conflicts and streamline your scheduling process.
                    </p>
                </div>
                <div className="mx-6 mb-6">
                    <TimezoneOperationhours ref={timeRef} />
                    <DatesOverride />
                </div>
            </div>
        );
    };

    const renderThirdStep = () => {
        return (
            <>
                <div className={s.welcomeHeader}>
                    <div className={s.iconWrapper}>
                        <img src={googleCalendarIcon} alt="Google Calendar" className={s.icon} />
                    </div>
                </div>

                <div className={s.welcomeContent}>
                    <h2 className={s.welcomeTitle}>Connect your Google Calendar</h2>
                    <p className={cls(s.welcomeText, 'mb-2')}>
                        Sync appointments between Kiwi Health and your personal Google Calendar to keep all your
                        events in one place and prevent double bookings.
                    </p>

                    <div className={s.welcomeText}>
                        <p>Here&apos;s how it works:</p>
                        <ul className={s.bulletList}>
                            <li>Kiwi appointments will show up as a separate sub-calendar in your Google Calendar.</li>
                            <li>Events from your personal calendar are synced as view-only in {calendarLabel}.</li>
                            <li>You can&apos;t cancel or modify Kiwi appointments directly in your personal calendar; any changes must be made within Kiwi Health.</li>
                        </ul>
                    </div>

                    <div className={s.connectButtons}>
                        <AuthButton className={s.connectButton} successCallbackPath={`${PATH.DASHBOARD}/${PATH.CALENDAR}`} callback={onClose} />
                        <Button className={s.skipButton} onClick={onClose}>Skip for now</Button>
                    </div>
                </div>
            </>
        );
    };

    let buttonText = 'Next';
    if (currentStep === 1 && isExistUser) {
        buttonText = 'Get started now';
    }

    return (
        <Modal
            open={isOpen}
            onCancel={onClose}
            maskClosable={false}
            footer={null}
            width={990}
            centered
            className={`${s.welcomeModal}`}
        >
            {
                [0, 1].includes(currentStep) && (
                    <div className={s.hWrap}>
                        <div className={s.left}>
                            <span className={s.label}>New</span>
                            <h2>Learn what’s new about {calendarLabel} ✨</h2>
                            <div className={s.des}>
                                We’ve added new features to help you save time and reduce no-shows.
                            </div>
                            <div className={currentStep === 0 ? `${s.module} ${s.moduleActive}` : s.module}>
                                <div className={s.mTitle}>🧑‍💻 New Patient Portal</div>
                                <div className={s.mDes}>When a new client books with you, they’ll be invited to your patient portal to enter personal details, provide credit card to secure their appointment, and manage their appointment. This means less manual follow-up and a smoother, more informed visit for you.</div>
                            </div>
                            <div className={currentStep === 1 ? `${s.module} ${s.moduleActive}` : s.module}>
                                <div className={s.mTitle}>🔒 Protected Appointments</div>
                                <div className={s.mDes}>Appointments become “Secured” once the client adds their credit card. This helps reduce last-minute cancellations and no-shows, giving you more reliable bookings and protecting your time.</div>
                            </div>
                            <div className={s.footer}>
                                <Button
                                    onClick={() => {
                                        if (currentStep === 0) {
                                            setCurrentStep(1);
                                        } else if (isExistUser || isShowKlarityUi) {
                                            onClose();
                                        } else {
                                            setCurrentStep(2);
                                        }
                                    }}
                                    type="primary"
                                    size="large"
                                    block
                                >{buttonText}
                                </Button>
                            </div>
                        </div>
                        <div className={s.right}>
                            {
                                currentStep === 0 && <img src={Step1} alt="Step 1" className={s.stepImg} />
                            }
                            {
                                currentStep === 1 && <img src={Step2} alt="Step 2" className={s.stepImg} />
                            }

                        </div>
                    </div>
                )
            }
            {
                currentStep === 2 && renderSecondStep()
            }
            {
                currentStep === 3 && renderThirdStep()
            }
            {
                [2, 3].includes(currentStep) && (
                    <div className={s.welcomeFooter}>
                        <div className={s.stepIndicator}>Step {currentStep - 1} of 2</div>
                        <div>
                            {
                                showBackButton && currentStep >= 1 && <Button className={s.operationButton} onClick={() => setCurrentStep((prev) => prev - 1)}>Back</Button>
                            }
                            <Button
                                type="primary"
                                className={s.operationButton}
                                onClick={async () => {
                                    if (currentStep === 2 && timeRef.current) {
                                        timeRef.current?.onUpdate();
                                    }
                                    if (currentStep === 3) {
                                        onClose();
                                    } else {
                                        setCurrentStep((prev) => prev + 1);
                                    }
                                }}
                            >
                                {currentStep === 3 ? 'Finish' : 'Next'}
                            </Button>
                        </div>
                    </div>
                )
            }
        </Modal>
    );
};

export default WelcomeModal;
